import {
  Content as DropdownContent,
  Portal as DropdownPortal,
  Root as DropdownRoot,
  Separator as DropdownSeparator,
} from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import React, {useCallback, useState} from 'react';

import {useIsInLoadedReportContext} from '../../../pages/ReportPage/LoadedReportContext';
import {
  RunMenuAction,
  trackRunMenu,
} from '../../../services/analytics/runMenuEvents';
import {RunRenameScope} from '../types';
import {RowHeaderMenuOption} from './RowHeaderMenuOption';
import {RowHeaderMenuTrigger} from './RowHeaderMenuTrigger';

type RunRowHeaderMenuProps = {
  onBenchmarkRun: () => void;
  onDeleteRun: () => void;
  onMoveRun: () => void;
  onRenameRun: (scope: RunRenameScope) => void;
  onStopRun: () => void;

  canBenchmarkRun?: boolean;
  canStopRun?: boolean;
};

export function RunRowHeaderMenu(props: RunRowHeaderMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isReport = useIsInLoadedReportContext();

  const trackRunMenuWithLocation = useCallback(
    (action: RunMenuAction) => {
      trackRunMenu({action, location: isReport ? 'report' : 'workspace'});
    },
    [isReport]
  );

  return (
    <DropdownRoot open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
      <RowHeaderMenuTrigger open={menuOpen} setMenuOpen={setMenuOpen} />
      <DropdownPortal>
        <DropdownContent
          align="start"
          sideOffset={8}
          alignOffset={-8}
          style={{zIndex: 1}}>
          {isReport ? (
            <>
              <RowHeaderMenuOption
                icon={<Icon name="pencil-edit" />}
                label={
                  <span>
                    Rename run for{' '}
                    <span className="font-semibold">project</span>
                  </span>
                }
                onSelect={() => {
                  props.onRenameRun('project');
                  trackRunMenuWithLocation('Rename run (project)');
                }}
              />
              <RowHeaderMenuOption
                icon={<Icon name="pencil-edit" />}
                label={
                  <span>
                    Rename run for{' '}
                    <span className="font-semibold">panel grid</span>
                  </span>
                }
                onSelect={() => {
                  props.onRenameRun('view-section');
                  trackRunMenuWithLocation('Rename run (custom run name)');
                }}
              />
              <DropdownSeparator />
            </>
          ) : (
            <RowHeaderMenuOption
              icon={<Icon name="pencil-edit" />}
              label="Rename run"
              onSelect={() => {
                props.onRenameRun('project');
                trackRunMenuWithLocation('Rename run (project)');
              }}
            />
          )}
          <RowHeaderMenuOption
            icon={<Icon name="forward-next" />}
            label="Move to another group"
            onSelect={() => {
              props.onMoveRun();
              trackRunMenuWithLocation('Move run groups');
            }}
          />
          <RowHeaderMenuOption
            icon={<Icon name="delete" />}
            label="Delete run"
            onSelect={() => {
              props.onDeleteRun();
              trackRunMenuWithLocation('Delete run');
            }}
          />
          {props.canBenchmarkRun ? (
            <RowHeaderMenuOption
              icon={<Icon name="benchmark-square" />}
              label="Submit to benchmark"
              onSelect={props.onBenchmarkRun}
            />
          ) : null}
          {props.canStopRun ? (
            <RowHeaderMenuOption
              icon={<Icon name="stop" />}
              label="Stop run"
              onSelect={() => {
                props.onStopRun();
                trackRunMenuWithLocation('Stop run');
              }}
            />
          ) : null}
        </DropdownContent>
      </DropdownPortal>
    </DropdownRoot>
  );
}
